import React, { useEffect, useState, useRef } from 'react';
import {
  updateDuration,
  updateInitialPayment,
  updateYearlyMileage,
} from '@store/reducers/vehiclesearchparams';
import { useAppDispatch, useAppSelector } from '@hooks/statehooks';
import { v4 as uuid4 } from 'uuid';
import cn from 'classnames';
import ReactSlider from 'react-slider';
import Tooltip from '@components/tooltip';
import Typography from '@components/typography';

import '@styles/configure/quickaction.scss';
import { usePricing } from '@hooks/usepricing';
import { QuickActionProps } from 'interface';
import { useDebounce } from '@hooks/usedebounce';

const QuickAction: React.FC<QuickActionProps> = ({
  variant,
  maxValue,
  midValue,
  minValue,
  rangeMaxValue,
  rangeMinValue,
  stepValue,
  testId,
  title,
  tooltipText,
  defaultValue,
}): JSX.Element => {
  const customLabel: string = 'Custom';
  const durationVariant: string = 'duration';
  const milesVariant: string = 'miles';
  const paymentVariant: string = 'payment';
  const pricingState = useAppSelector((state) => state.pricingState);
  const { pricingType } = useAppSelector((state) => state.vehicleInformation);

  const dispatch = useAppDispatch();

  // Set initial price from API, or calculate based monthly payments
  const pricingData: (string | number)[] = [
    minValue,
    midValue,
    maxValue,
    customLabel,
  ];

  // If a non-selectable number is present, select Custom and show the slider with the value
  const getInitialSelection = (): number | string =>
    pricingData.includes(defaultValue) ? defaultValue : customLabel;

  // These always come in as numbers from the API, the only time it changes is if we set Custom.
  const [selectedDuration, setSelectedDuration] = useState<number | string>(
    getInitialSelection()
  );
  const formatPriceValue = (value: number | string) => {
    return value?.toLocaleString('en-GB');
  };
  const [currentValue, setCurrentValue] = useState<number>(defaultValue);
  const [currentInputValue, setCurrentInputValue] = useState<string>(
    formatPriceValue(defaultValue)
  );

  const maxLength = formatPriceValue(rangeMaxValue)
    ? formatPriceValue(rangeMaxValue).length
    : 6;

  const isMounted = useRef(false);

  const { updateAllPricing } = usePricing();

  const validateInitialPayment = (
    valueToValidate: string,
    updateSelectedDuration: boolean = true
  ) => {
    let inputValueNumber = Number(valueToValidate.replace(/,/g, ''));
    if (inputValueNumber < rangeMinValue) {
      inputValueNumber = rangeMinValue;
    } else if (inputValueNumber > rangeMaxValue) {
      inputValueNumber = rangeMaxValue;
    }
    dispatch(updateInitialPayment(inputValueNumber));
    setCurrentInputValue(formatPriceValue(inputValueNumber));
    setCurrentValue(inputValueNumber);

    if (updateSelectedDuration) {
      setSelectedDuration(
        pricingData.includes(inputValueNumber) ? inputValueNumber : customLabel
      );
    }
  };

  const quickkActionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Stops the component making an initial API call because searchParams is updated elsewhere and triggers this effect
    if (isMounted.current) {
      updateAllPricing(pricingType);
      return;
    }
    isMounted.current = true;
  }, [currentValue]);

  useEffect(() => {
    if (isMounted.current) {
      if (variant === paymentVariant) {
        validateInitialPayment(defaultValue.toString());
      }
      return;
    }
    isMounted.current = true;
  }, [rangeMaxValue, rangeMinValue]);

  const onSelectedDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueToNumber = Number(e.target.value);

    // Check if not Custom
    if (e.target.value !== customLabel) {
      switch (variant) {
        case durationVariant:
          dispatch(updateDuration(valueToNumber));
          setCurrentValue(valueToNumber);
          setSelectedDuration(valueToNumber);
          break;
        case milesVariant:
          dispatch(updateYearlyMileage(valueToNumber));
          setCurrentValue(valueToNumber);
          setSelectedDuration(valueToNumber);
          break;
        case paymentVariant:
          validateInitialPayment(e.target.value);
          break;
      }
    } else {
      // Should only be 'Custom' here
      setSelectedDuration(e.target.value);
    }
  };

  const onSliderAfterChange = (value: number) => {
    switch (variant) {
      case durationVariant:
        dispatch(updateDuration(value));
        break;
      case milesVariant:
        dispatch(updateYearlyMileage(value));
        break;
      case paymentVariant:
        dispatch(updateInitialPayment(value));
        break;
    }
    setCurrentValue(value);
  };

  const onSliderChange = (value: number) => {
    if (variant === paymentVariant) {
      setCurrentInputValue(formatPriceValue(value));
    }
  };

  const onCustomPriceSliderUpdate = async (inputValue: string) => {
    validateInitialPayment(inputValue, false);
  };

  const onChangeDebounced = useDebounce(onCustomPriceSliderUpdate, 3000);

  const onCustomPriceChange = async (text: string) => {
    setCurrentInputValue(formatPriceValue(text));
    onChangeDebounced(text);
  };

  const getOption = (value: number | string) => {
    const valueLowercase = value.toString().toLowerCase();
    const formattedValue = formatPriceValue(value);

    const inputClassNames = cn({
      'quickaction__option-input': 'quickaction__option-input',
      [`quickaction__option-input--${variant}`]: variant,
    });

    const labelClassNames = cn({
      'quickaction__option-label': 'quickaction__option-label',
      [`quickaction__option-label--${variant}`]: variant,
    });

    return (
      <React.Fragment key={uuid4()}>
        <input
          className={inputClassNames}
          data-cy={`${testId}-option-input-${valueLowercase}`}
          data-testid={`${testId}-option-input-${valueLowercase}`}
          id={`option-${valueLowercase}-${variant}`}
          onChange={onSelectedDurationChange}
          type="radio"
          name={variant}
          value={value}
          checked={selectedDuration === value}
        />
        <label
          className={labelClassNames}
          data-cy={`${testId}-option-label-${valueLowercase}`}
          data-testid={`${testId}-option-label-${valueLowercase}`}
          htmlFor={`option-${valueLowercase}-${variant}`}
        >
          <Typography size="sm" variant="body1" weight="semi-bold">
            {variant === paymentVariant && value !== customLabel
              ? `£${formattedValue}`
              : formattedValue}
          </Typography>
        </label>
      </React.Fragment>
    );
  };

  const getSlider = (): React.ReactElement => {
    if (selectedDuration !== customLabel) {
      return <></>;
    }

    // Add a £ if the range slider is for payment only
    const rangeDecorator = (value: string | number): string => {
      const formattedValue = formatPriceValue(value);
      return variant === paymentVariant ? `£${formattedValue}` : formattedValue;
    };

    return (
      <div
        className={cn(
          'quickaction__slider',
          variant === paymentVariant && 'quickaction__slider-leasepayment'
        )}
        data-cy={`${testId}-slider`}
        data-testid={`${testId}-slider`}
      >
        <div className="quickaction__slider-content">
          <ReactSlider
            className="quickaction__slider-component"
            ariaLabel="slider"
            disabled={!pricingState.finished}
            value={currentValue}
            onAfterChange={onSliderAfterChange}
            onChange={onSliderChange}
            thumbClassName={`quickaction__${variant}-slider-thumb`}
            trackClassName={`quickaction__${variant}-slider-track`}
            min={rangeMinValue}
            max={rangeMaxValue}
            step={stepValue}
            renderThumb={(props, state) => (
              <div {...props}>
                <Typography size="sm" variant="body1" weight="semi-bold">
                  {variant === paymentVariant
                    ? ''
                    : rangeDecorator(state.valueNow)}
                </Typography>
              </div>
            )}
          />
        </div>
        <div
          className="quickaction__slider-labels"
          data-cy={`${testId}-slider-labels`}
          data-testid={`${testId}-slider-labels`}
        >
          <Typography size="sm" variant="body1">
            {rangeDecorator(rangeMinValue)}
          </Typography>
          <Typography size="sm" variant="body1">
            {rangeDecorator(rangeMaxValue)}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <fieldset
      className="quickaction__container"
      data-cy={`${testId}-container`}
      data-testid={`${testId}-container`}
    >
      <legend
        className="quickaction__header"
        data-cy={`${testId}-header`}
        data-testid={`${testId}-header`}
      >
        <Typography size="rg" variant="h3" weight="bold">
          {title}
        </Typography>
        <Tooltip
          id={variant}
          text={''}
          delayHide={100}
          data-cy={`${testId}-tooltip`}
        >
          <Typography variant="caption" size="xs" colour="alternative">
            {tooltipText}
          </Typography>
        </Tooltip>
      </legend>
      <div
        className="quickaction__options"
        data-cy={`${testId}-options`}
        data-testid={`${testId}-options`}
      >
        {pricingData.map((value) => getOption(value))}
      </div>
      <div ref={quickkActionRef}>
        {selectedDuration === customLabel && variant === paymentVariant && (
          <div
            className="quickaction__customprice"
            data-cy={`${testId}-customprice`}
            data-testid={`${testId}-customprice`}
          >
            <Typography variant="body2" size="sm" weight="semi-bold">
              How much would you like to pay?
            </Typography>
            <div className="quickaction__customprice-currency">
              <div className="quickaction__customprice-currency-symbol">£</div>
              <input
                data-cy={`${testId}-customprice-input`}
                data-testid={`${testId}-customprice-input`}
                id={`customprice-${variant}`}
                onChange={(e) => onCustomPriceChange(e.target.value)}
                type="text"
                maxLength={maxLength}
                name={variant}
                aria-label={`custom price ${variant}`}
                onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  } else {
                    const currentInput = (event.target as HTMLInputElement)
                      .value as string;
                    if (
                      !currentInput.includes(',') &&
                      currentInput.length === maxLength - 1
                    ) {
                      event.preventDefault();
                    }
                  }
                }}
                value={currentInputValue}
              />
            </div>
          </div>
        )}
        {getSlider()}
      </div>
    </fieldset>
  );
};

export default QuickAction;
